import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Select,
  FormControl,
  FormLabel,
  Center,
  Spinner,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  useMediaQuery,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

const Users = ({ isAuthenticated, onNotAuthenticated }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/users.php`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch available roles
  const fetchRoles = async () => {
    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/users.php?type=roles`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(Array.isArray(data.roles) ? data.roles : []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast({
        title: 'Error',
        description: 'Failed to load roles: ' + error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fetch users and roles on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
      fetchRoles();
    }
  }, [isAuthenticated]);

  // Handle form submission (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.role) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields (Email and Role)',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const method = editingId ? 'PUT' : 'POST';
      const payload = editingId
        ? { ...formData, id: editingId }
        : formData;

      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/users.php`, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save user');
      }

      const responseData = await response.json();

      toast({
        title: 'Success',
        description: editingId ? 'User updated successfully' : 'User created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Update local state instead of refetching to avoid screen flash
      // Handle both response formats: { user: {...} } or just {...}
      const userData = responseData.user || responseData;
      
      if (editingId) {
        // Update existing user
        setUsers(users.map(u => u.id === editingId ? userData : u));
      } else {
        // Add new user
        setUsers([...users, userData]);
      }

      setFormData({ email: '', name: '', role: '' });
      setEditingId(null);
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle edit
  const handleEdit = (user) => {
    const userRole = user.roles && user.roles.length > 0 ? user.roles[0] : '';
    setFormData({ email: user.email, name: user.name || '', role: userRole });
    setEditingId(user.id);
    onOpen();
  };

  // Handle delete
  const handleDeleteClick = (user) => {
    setDeleteTarget(user);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/users.php`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: deleteTarget.id }),
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      toast({
        title: 'Success',
        description: 'User deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Remove user from local state instead of refetching to avoid screen flash
      setUsers(users.filter(u => u.id !== deleteTarget.id));
      setDeleteTarget(null);
      onDeleteClose();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setFormData({ email: '', name: '', role: '' });
    setEditingId(null);
    onClose();
  };

  // Table columns
  const columns = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: info => info.getValue?.() || '—',
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: info => info.getValue?.() || '—',
    },
    {
      accessorKey: 'roles',
      header: 'Role',
      cell: info => {
        const roles = info.getValue?.();
        return roles && Array.isArray(roles) && roles.length > 0 ? roles[0] : '—';
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: info => {
        const dateStr = info.getValue?.();
        try {
          return dateStr ? new Date(dateStr).toLocaleDateString() : '—';
        } catch {
          return dateStr || '—';
        }
      },
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => (
        <HStack spacing={2}>
          <IconButton
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => handleEdit(info.row.original)}
            aria-label="Edit user"
          />
        </HStack>
      ),
    },
  ];

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)">
      <Container maxW={isMobile ? "100%" : "6xl"} px={isMobile ? 4 : 6} py={8}>
        <VStack align="start" spacing={6} mb={8}>
          {/* Header */}
          <HStack 
            justify="space-between" 
            width="100%" 
            flexDirection={isMobile ? "column" : "row"} 
            align={isMobile ? "start" : "center"} 
            spacing={isMobile ? 3 : 0}
          >
            <Heading 
              as="h1" 
              size={isMobile ? "lg" : "2xl"} 
              color="gray.900" 
              fontWeight="700"
            >
              Manage Users
            </Heading>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              size={isMobile ? "sm" : "md"}
              onClick={() => {
                setFormData({ email: '', name: '' });
                setEditingId(null);
                onOpen();
              }}
            >
              Add User
            </Button>
          </HStack>
        </VStack>

        {/* Loading State */}
        {loading ? (
          <Center py={10}>
            <Spinner size="lg" color="blue.500" />
          </Center>
        ) : users.length === 0 ? (
          <Center py={10}>
            <Text color="gray.500">No users found</Text>
          </Center>
        ) : (
          /* Users Table */
          <Box 
            bg="white" 
            borderRadius="lg" 
            boxShadow="md" 
            overflowX="auto"
          >
            <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
              <Thead bg="gray.100">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Th
                        key={header.id}
                        cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        color="gray.700"
                        fontWeight="600"
                        fontSize={isMobile ? "xs" : "sm"}
                      >
                        <HStack spacing={1}>
                          <Text>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Text>
                          {header.column.getCanSort() && (
                            <Text fontSize="xs">
                              {header.column.getIsSorted()
                                ? header.column.getIsSorted() === 'desc'
                                  ? ' 🔽'
                                  : ' 🔼'
                                : ''}
                            </Text>
                          )}
                        </HStack>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => (
                  <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                    {row.getVisibleCells().map(cell => (
                      <Td key={cell.id} fontSize={isMobile ? "xs" : "sm"}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Container>

      {/* Add/Edit User Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingId ? 'Edit User' : 'Add New User'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Role</FormLabel>
                  <Select
                    placeholder="Select a role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleModalClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" type="submit">
                {editingId ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete User
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget?.email}</strong>? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Users;
